import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  MagnifyingGlass, EnvelopeSimple, Star, ShieldCheck,
  Lightning, Money, CurrencyGbp, X, CheckCircle, Globe, Trash,
} from "@phosphor-icons/react";
import AdminLayout from "../components/AdminLayout";

const STATUS_LABEL = {
  none: { label: "—", className: "text-[var(--text-secondary)]" },
  trialing: { label: "Trialing", className: "text-amber-700 bg-amber-50" },
  active: { label: "Pro", className: "text-[var(--primary)] bg-[var(--accent-sand)]" },
  past_due: { label: "Past due", className: "text-red-700 bg-red-50" },
  cancelled: { label: "Cancelled", className: "text-[var(--text-secondary)] bg-gray-100" },
};

export default function Admin() {
  // Bail out BEFORE any data-fetching useEffects fire when the visitor isn't
  // an admin — stops the dev overlay flashing dozens of 403s and short-
  // circuits the redirect chain AdminLayout would otherwise do.
  const { user, loading: authLoading } = useAuth();
  if (authLoading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/admin/login?reason=not-admin" replace />;
  return <AdminInner />;
}

function AdminInner() {
  // Tab is driven by the URL — /admin → users, /admin/pricing → pricing, etc.
  const loc = useLocation();
  const path = loc.pathname.replace(/\/+$/, "");
  const tab =
    path === "/admin/pricing" ? "pricing" :
    path === "/admin/coupons" ? "coupons" :
    path === "/admin/domain" ? "domain" :
    path === "/admin/revenue" ? "revenue" :
    path === "/admin/submissions" ? "submissions" :
    path === "/admin/course-data" ? "course-data" :
    path === "/admin/backups" ? "backups" :
    "users";
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [cfg, setCfg] = useState(null);
  const [domain, setDomain] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [pinSubmissions, setPinSubmissions] = useState([]);
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadUsers = useCallback(async (search = "") => {
    const { data } = await api.get(`/admin/users${search ? `?q=${encodeURIComponent(search)}` : ""}`);
    setUsers(data);
  }, []);

  const loadCfg = useCallback(async () => {
    const { data } = await api.get("/subscription/config");
    setCfg(data);
  }, []);

  const loadDomain = useCallback(async () => {
    const { data } = await api.get("/domain");
    setDomain(data);
  }, []);

  const loadRevenue = useCallback(async () => {
    const { data } = await api.get("/admin/revenue");
    setRevenue(data);
  }, []);

  const loadCoupons = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data?.coupons || []);
    } catch {
      /* non-fatal — admin not logged in yet etc */
    }
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      const [stats, pins] = await Promise.all([
        api.get("/admin/course-submissions"),
        api.get("/admin/pin-submissions"),
      ]);
      setSubmissions(stats.data?.submissions || []);
      setPinSubmissions(pins.data?.submissions || []);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => { loadUsers(); loadCfg(); loadDomain(); loadRevenue(); loadCoupons(); loadSubmissions(); }, [loadUsers, loadCfg, loadDomain, loadRevenue, loadCoupons, loadSubmissions]);

  const sendReset = async (u) => {
    if (!window.confirm(`Send password reset link to ${u.email}?`)) return;
    setError("");
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-reset`);
      setToast(data.sent ? `Reset link emailed to ${u.email}` : `Email failed — check Resend dashboard for ${u.email}`);
      setTimeout(() => setToast(""), 4000);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\n\nThis removes their rounds, scores, and league memberships. Can't be undone.`)) return;
    setError("");
    try {
      // 1) Fire the delete
      const { data } = await api.delete(`/admin/users/${u.id}`);
      if (!data?.ok) {
        throw new Error("Server didn't confirm the delete — please refresh and try again.");
      }
      // 2) Verify by searching for the email. If it comes back, we've got a ghost record.
      const { data: check } = await api.get(`/admin/users?q=${encodeURIComponent(u.email)}`);
      const stillThere = (check || []).some((x) => x.email?.toLowerCase() === u.email?.toLowerCase());
      if (stillThere) {
        setError(`Delete reported OK but ${u.email} is still in the database. Try once more, or contact support.`);
        await loadUsers(q);
        return;
      }
      // 3) Success — visible toast that auto-clears
      setToast(`✓ ${u.email} deleted`);
      setTimeout(() => setToast(""), 3500);
      await loadUsers(q);
      await loadRevenue();
    } catch (err) {
      setError(`Couldn't delete ${u.email}: ${formatApiError(err)}`);
    }
  };

  const purgeTestUsers = async () => {
    const count = users.filter((u) => u.role !== "admin").length;
    if (count === 0) { setToast("Nothing to purge"); setTimeout(() => setToast(""), 2000); return; }
    if (!window.confirm(`⚠ Delete ALL ${count} non-admin users and their data?\n\nThis is PERMANENT — your real customers will lose accounts, rounds, leagues. Only use BEFORE launch for test accounts.\n\nClick OK if you really mean it.`)) return;
    const typed = window.prompt(`Final check: type the word PURGE (in capitals) to delete ${count} users.`);
    if (typed !== "PURGE") { setToast("Cancelled — text didn't match."); setTimeout(() => setToast(""), 3000); return; }
    try {
      const { data } = await api.post("/admin/users/purge-test", { confirm: "PURGE", force: count > 50 });
      setToast(`Purged ${data.deleted} users`);
      setTimeout(() => setToast(""), 4000);
      await loadUsers(q);
      await loadRevenue();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const userAction = async (u, action) => {
    const confirmMsg = action === "grant_pro" ? `Grant Fairway Pro to ${u.email}?` : `Revoke Pro from ${u.email}?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post(`/admin/users/${u.id}/action`, { action });
      setToast(`${u.email} → ${action === "grant_pro" ? "granted Pro" : "Pro revoked"}`);
      setTimeout(() => setToast(""), 3000);
      await loadUsers(q);
      await loadRevenue();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const saveCfg = async (patch) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.patch("/admin/subscription/config", patch);
      setCfg(data);
      setToast("Pricing updated");
      setTimeout(() => setToast(""), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const saveDomain = async (primaryDomain) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.patch("/admin/domain", { primary_domain: primaryDomain });
      setDomain(data);
      setToast(primaryDomain ? "Domain saved" : "Domain cleared");
      setTimeout(() => setToast(""), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const TITLES = {
    users: ["Users", "Search, send reset links, grant Pro."],
    pricing: ["Pricing", "Set price, currency & trial length."],
    coupons: ["Coupons", "Create discount or freebie codes for your subscription."],
    domain: ["Domain", "Point your custom domain at the app."],
    revenue: ["Revenue", "Subscribers and recent payments."],
    submissions: ["Submissions", "Approve crowdsourced course stats from players."],
    "course-data": ["Course data", "Auto-pull real scorecards from GolfCourseAPI."],
    backups: ["Backups", "Download a full DB snapshot or restore from one. Your safety net."],
  };
  const [title, subtitle] = TITLES[tab];

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-black tracking-tighter-2 text-3xl sm:text-4xl text-[var(--primary)]">{title}</h1>
          <p className="text-[var(--text-secondary)] text-sm">{subtitle}</p>
        </div>
      </div>

      {toast && (
        <div
          className="rounded-xl px-3 py-2 mb-3 text-sm font-bold flex items-center gap-2"
          style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
          data-testid="admin-toast"
        >
          <CheckCircle size={16} weight="fill" /> {toast}
        </div>
      )}
      {error && (
        <div className="rounded-xl px-3 py-2 mb-3 text-sm bg-red-50 border border-red-200 text-[var(--status-bad)]" data-testid="admin-error">
          {error}
        </div>
      )}

      {tab === "users" && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); loadUsers(e.target.value); }}
                placeholder="Search name or email..."
                className="input-field pl-11 pr-12"
                data-testid="admin-user-search"
              />
            </div>
            <button
              type="button"
              onClick={() => loadUsers(q)}
              data-testid="admin-users-refresh"
              className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg whitespace-nowrap"
              style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
              title="Fetch the latest user list from the server"
            >
              ↻ Refresh
            </button>
            <button
              onClick={purgeTestUsers}
              data-testid="admin-purge-test-users"
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-2 rounded-lg whitespace-nowrap opacity-50 hover:opacity-100 transition-opacity"
              style={{ background: "transparent", color: "var(--accent-terracotta)", border: "1px dashed rgba(196,84,60,0.3)" }}
              title="DANGER — Permanently deletes every non-admin user, their rounds, and leagues. Requires typing PURGE to confirm."
            >
              ⚠ Purge…
            </button>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mb-3">{users.length} matching · most recent first</p>

          <div className="space-y-2" data-testid="admin-users-list">
            {users.map((u) => {
              const stat = STATUS_LABEL[u.subscription_status] || STATUS_LABEL.none;
              const isAdmin = u.role === "admin";
              return (
                <div key={u.id} className="surface-card-flat p-3" data-testid={`admin-user-row-${u.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-heading font-bold text-[var(--primary)] text-sm truncate">{u.name || "—"}</span>
                        {isAdmin && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[var(--accent-terracotta)] text-white">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--text-secondary)] truncate">{u.email}</div>
                      <div className="text-[10px] text-[var(--text-secondary)] mt-1">
                        Joined {u.created_at?.slice(0, 10) || "—"}
                        {u.handicap != null && ` · HCP ${u.handicap}`}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${stat.className} shrink-0`}>
                      {stat.label}
                    </span>
                  </div>
                  <div className="flex gap-1 flex-wrap mt-2">
                    <button
                      onClick={() => sendReset(u)}
                      data-testid={`admin-send-reset-${u.id}`}
                      className="text-xs font-bold font-heading px-2.5 py-1.5 rounded-lg flex items-center gap-1"
                      style={{ background: "var(--bg-app)", color: "var(--primary)" }}
                    >
                      <EnvelopeSimple size={12} weight="bold" /> Send reset link
                    </button>
                    {u.subscription_status !== "active" ? (
                      <button
                        onClick={() => userAction(u, "grant_pro")}
                        data-testid={`admin-grant-${u.id}`}
                        className="text-xs font-bold font-heading px-2.5 py-1.5 rounded-lg flex items-center gap-1"
                        style={{ background: "var(--accent-terracotta)", color: "#F2EEE7" }}
                      >
                        <Star size={12} weight="fill" /> Grant Pro
                      </button>
                    ) : (
                      !isAdmin && (
                        <button
                          onClick={() => userAction(u, "revoke_pro")}
                          data-testid={`admin-revoke-${u.id}`}
                          className="text-xs font-bold font-heading px-2.5 py-1.5 rounded-lg flex items-center gap-1"
                          style={{ background: "var(--bg-app)", color: "var(--primary)" }}
                        >
                          <X size={12} weight="bold" /> Revoke
                        </button>
                      )
                    )}
                    {!isAdmin && (
                      <button
                        onClick={() => deleteUser(u)}
                        data-testid={`admin-delete-${u.id}`}
                        className="text-xs font-bold font-heading px-2.5 py-1.5 rounded-lg flex items-center gap-1 ml-auto"
                        style={{ background: "rgba(196,84,60,0.1)", color: "var(--accent-terracotta)" }}
                      >
                        <Trash size={12} weight="bold" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "pricing" && cfg && (
        <PricingEditor cfg={cfg} loading={loading} onSave={saveCfg} />
      )}

      {tab === "coupons" && (
        <CouponsManager
          coupons={coupons}
          onCreate={async (payload) => {
            setError("");
            try {
              await api.post("/admin/coupons", payload);
              setToast(`Code ${payload.code} created`);
              setTimeout(() => setToast(""), 3000);
              await loadCoupons();
              return true;
            } catch (err) {
              setError(formatApiError(err));
              return false;
            }
          }}
          onDeactivate={async (c) => {
            if (!window.confirm(`Deactivate ${c.code}? Existing users who already redeemed it keep their subscription, but no-one new can use it.`)) return;
            try {
              await api.delete(`/admin/coupons/${c.id}`);
              setToast(`${c.code} deactivated`);
              setTimeout(() => setToast(""), 3000);
              await loadCoupons();
            } catch (err) {
              setError(formatApiError(err));
            }
          }}
          onPurgeInactive={async () => {
            if (!window.confirm("Delete ALL deactivated coupons permanently? This can't be undone.")) return;
            try {
              const { data } = await api.delete("/admin/coupons");
              setToast(`Deleted ${data?.deleted ?? 0} deactivated coupon(s)`);
              setTimeout(() => setToast(""), 3000);
              await loadCoupons();
            } catch (err) {
              setError(formatApiError(err));
            }
          }}
        />
      )}

      {tab === "domain" && domain && (
        <DomainEditor domain={domain} loading={loading} onSave={saveDomain} />
      )}

      {tab === "submissions" && (
        <div className="space-y-6" data-testid="admin-submissions">
          <div>
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-[var(--text-secondary)] mb-3">
              Course stats · {submissions.length}
            </h3>
            <div className="space-y-3">
              {submissions.length === 0 ? (
                <p className="surface-card-flat p-4 text-sm text-[var(--text-secondary)]">
                  No pending course-stat submissions.
                </p>
              ) : (
                submissions.map((s) => (
              <div
                key={s.id}
                data-testid={`submission-row-${s.id}`}
                className="surface-card p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-base text-[var(--primary)] truncate">
                    {s.course_name}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                    From <span className="font-bold">{s.user_name || s.user_email}</span> · {new Date(s.submitted_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span><span className="label-uppercase">Par</span> <span className="font-mono-num font-bold ml-1">{s.par_total}</span></span>
                    <span><span className="label-uppercase">Yds</span> <span className="font-mono-num font-bold ml-1">{s.yards_total}</span></span>
                    {s.holes && (
                      <span className="text-xs text-[var(--text-secondary)] italic">
                        + {s.holes.length} hole detail{s.holes.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.post(`/admin/course-submissions/${s.id}/approve`);
                        setToast(`Approved · ${s.course_name}`);
                        setTimeout(() => setToast(""), 3000);
                        await loadSubmissions();
                      } catch (err) {
                        setError(formatApiError(err));
                      }
                    }}
                    data-testid={`submission-approve-${s.id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-green-500/15 text-green-700 hover:bg-green-500/25 border border-green-500/40"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.post(`/admin/course-submissions/${s.id}/reject`);
                        setToast(`Rejected · ${s.course_name}`);
                        setTimeout(() => setToast(""), 3000);
                        await loadSubmissions();
                      } catch (err) {
                        setError(formatApiError(err));
                      }
                    }}
                    data-testid={`submission-reject-${s.id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/30"
                  >
                    Reject
                  </button>
                </div>
              </div>
                ))
              )}
            </div>
          </div>

          <div>
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-[var(--text-secondary)] mb-3">
              Pin locations · {pinSubmissions.length}
            </h3>
            <div className="space-y-3">
              {pinSubmissions.length === 0 ? (
                <p className="surface-card-flat p-4 text-sm text-[var(--text-secondary)]">
                  No pending pin-location submissions. Small refinements auto-approve and never hit this queue.
                </p>
              ) : (
                pinSubmissions.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`pin-row-${p.id}`}
                    className="surface-card p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-base text-[var(--primary)] truncate">
                        {p.course_name} · Hole {p.hole_number}
                      </div>
                      <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                        From <span className="font-bold">{p.user_name || p.user_email}</span> · {new Date(p.submitted_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-[var(--text-secondary)] mt-2 font-mono-num">
                        {p.lat.toFixed(5)}, {p.lng.toFixed(5)}{" "}
                        <a
                          href={`https://www.google.com/maps/?q=${p.lat},${p.lng}&z=19&t=k`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[var(--primary)] underline ml-2 font-bold"
                          data-testid={`pin-map-link-${p.id}`}
                        >
                          View on map →
                        </a>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.post(`/admin/pin-submissions/${p.id}/approve`);
                            setToast(`Pin approved · ${p.course_name} h${p.hole_number}`);
                            setTimeout(() => setToast(""), 3000);
                            await loadSubmissions();
                          } catch (err) { setError(formatApiError(err)); }
                        }}
                        data-testid={`pin-approve-${p.id}`}
                        className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-green-500/15 text-green-700 hover:bg-green-500/25 border border-green-500/40"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.post(`/admin/pin-submissions/${p.id}/reject`);
                            setToast(`Pin rejected · ${p.course_name} h${p.hole_number}`);
                            setTimeout(() => setToast(""), 3000);
                            await loadSubmissions();
                          } catch (err) { setError(formatApiError(err)); }
                        }}
                        data-testid={`pin-reject-${p.id}`}
                        className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/30"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "revenue" && revenue && (
        <div className="space-y-4" data-testid="admin-revenue">
          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={<Money size={20} weight="fill" />} label="Total collected" value={`£${Number(revenue.total_collected).toFixed(2)}`} />
            <StatCard icon={<Lightning size={20} weight="fill" />} label="Active Pro" value={revenue.active_subscribers} />
            <StatCard icon={<CurrencyGbp size={20} weight="fill" />} label="Trialing" value={revenue.trialing_subscribers} />
            <StatCard icon={<X size={20} weight="bold" />} label="Cancelled" value={revenue.cancelled_subscribers} />
          </div>
          <h3 className="font-heading font-bold text-[var(--primary)] mt-6 mb-2 text-sm">Last 10 payments</h3>
          {revenue.recent_payments.length === 0 ? (
            <p className="text-sm text-[var(--text-secondary)]">No payments yet — once a user subscribes via Stripe they'll show here.</p>
          ) : (
            <div className="surface-card-flat overflow-hidden">
              {revenue.recent_payments.map((p, i) => (
                <div key={p.id || `${p.completed_at}-${i}`} className="flex justify-between items-center px-3 py-2 border-b border-[var(--border-default)] last:border-0 text-sm">
                  <span className="font-mono-num">£{Number(p.amount).toFixed(2)}</span>
                  <span className="text-xs text-[var(--text-secondary)]">{p.completed_at?.slice(0, 10) || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "course-data" && (
        <CourseDataPanel onToast={(msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); }} onError={setError} />
      )}

      {tab === "backups" && (
        <BackupsPanel onToast={(msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); }} onError={setError} />
      )}
    </AdminLayout>
  );
}

function PricingEditor({ cfg, loading, onSave }) {
  const [price, setPrice] = useState(cfg.price_amount);
  const [trial, setTrial] = useState(cfg.trial_days);
  const [currency, setCurrency] = useState(cfg.price_currency);
  const [name, setName] = useState(cfg.plan_name);
  const [active, setActive] = useState(cfg.active);

  useEffect(() => {
    setPrice(cfg.price_amount); setTrial(cfg.trial_days); setCurrency(cfg.price_currency); setName(cfg.plan_name); setActive(cfg.active);
  }, [cfg]);

  return (
    <div className="surface-card p-5 space-y-4" data-testid="admin-pricing-panel">
      <div>
        <label className="label-uppercase block mb-2">Plan name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input-field"
          data-testid="admin-plan-name"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-uppercase block mb-2">Price</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
            className="input-field font-mono-num"
            data-testid="admin-plan-price"
          />
        </div>
        <div>
          <label className="label-uppercase block mb-2">Currency</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="input-field"
            data-testid="admin-plan-currency"
          >
            <option value="gbp">GBP £</option>
            <option value="usd">USD $</option>
            <option value="eur">EUR €</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label-uppercase block mb-2">Free trial days</label>
        <input
          type="number"
          min="0"
          max="60"
          value={trial}
          onChange={(e) => setTrial(parseInt(e.target.value || 0, 10))}
          className="input-field font-mono-num"
          data-testid="admin-plan-trial"
        />
        <p className="text-xs text-[var(--text-secondary)] mt-1">0–60 days. Set 0 to charge immediately.</p>
      </div>
      <label className="flex items-center justify-between p-3 rounded-xl border border-[var(--border-default)] cursor-pointer">
        <div>
          <div className="font-heading font-bold text-[var(--primary)] text-sm">Accept new sign-ups</div>
          <div className="text-xs text-[var(--text-secondary)]">Toggle off to pause the Subscribe page without deleting the plan.</div>
        </div>
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-5 w-5 accent-[var(--accent-terracotta)]"
          data-testid="admin-plan-active"
        />
      </label>
      <button
        onClick={() => onSave({ price_amount: price, price_currency: currency, trial_days: trial, plan_name: name, active })}
        disabled={loading}
        className="btn-primary w-full"
        data-testid="admin-save-pricing"
      >
        {loading ? "Saving..." : "Save changes"}
      </button>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="surface-card-flat p-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-2"
        style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
      >
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)]">{label}</div>
      <div className="font-mono-num font-black text-xl text-[var(--primary)]">{value}</div>
    </div>
  );
}

function DomainEditor({ domain, loading, onSave }) {
  const [draft, setDraft] = useState(domain.primary_domain || "");
  const [copied, setCopied] = useState("");

  useEffect(() => { setDraft(domain.primary_domain || ""); }, [domain]);

  const currentHost = (() => {
    try {
      return new URL(domain.canonical_url || window.location.origin).hostname;
    } catch { return ""; }
  })();
  const isCustom = !!domain.primary_domain;

  const copy = (txt, key) => {
    navigator.clipboard?.writeText(txt);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="space-y-4" data-testid="admin-domain-panel">
      <div className="surface-card p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: isCustom ? "var(--accent-terracotta)" : "var(--accent-sand)",
              color: isCustom ? "#F2EEE7" : "var(--primary)",
            }}
          >
            <Globe size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)]">Live URL</div>
            <div
              className="font-mono-num font-bold text-sm text-[var(--primary)] truncate"
              data-testid="admin-domain-canonical"
            >
              {domain.canonical_url}
            </div>
          </div>
          <span
            className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded"
            style={{
              background: isCustom ? "var(--accent-sand)" : "rgba(20,61,47,0.06)",
              color: "var(--primary)",
            }}
          >
            {isCustom ? "Custom" : "Default"}
          </span>
        </div>

        <div>
          <label className="label-uppercase block mb-2">Primary domain</label>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="fairwaygolf.uk"
            className="input-field"
            data-testid="admin-domain-input"
          />
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Just the host — no <code>https://</code> needed. Leave empty to revert to the default URL.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onSave(draft.trim())}
            disabled={loading}
            className="btn-primary flex-1"
            data-testid="admin-domain-save"
          >
            {loading ? "Saving..." : "Save"}
          </button>
          {isCustom && (
            <button
              onClick={() => { setDraft(""); onSave(""); }}
              disabled={loading}
              className="btn-secondary"
              data-testid="admin-domain-clear"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="surface-card p-5">
        <h3 className="font-heading font-bold text-[var(--primary)] mb-1">DNS setup</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-4">
          Once you've bought a domain (e.g. at <strong>Namecheap</strong>, <strong>Porkbun</strong>, or <strong>Cloudflare</strong>),
          paste these records into the registrar's DNS panel.
        </p>

        <DnsRecord
          label="Apex (root)"
          type="CNAME"
          host="@"
          value={currentHost}
          copied={copied === "apex"}
          onCopy={() => copy(currentHost, "apex")}
        />
        <DnsRecord
          label="www subdomain"
          type="CNAME"
          host="www"
          value={currentHost}
          copied={copied === "www"}
          onCopy={() => copy(currentHost, "www")}
        />

        <div className="text-[11px] text-[var(--text-secondary)] mt-3 leading-relaxed">
          <strong>Heads up:</strong> some registrars don't allow CNAME on apex —
          use an <strong>ALIAS</strong> or <strong>ANAME</strong> record instead, or set both <code>@</code> and{" "}
          <code>www</code> to point to <code>www</code>. DNS can take up to 24h to propagate
          (usually 5–15 min). Once DNS resolves to this app, click <strong>Save</strong> above so
          the password-reset emails and share links use your new domain.
        </div>
      </div>
    </div>
  );
}

function DnsRecord({ label, type, host, value, copied, onCopy }) {
  return (
    <div className="bg-[var(--bg-app)] rounded-xl p-3 mb-2">
      <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)] mb-1">{label}</div>
      <div className="grid grid-cols-[60px_60px_1fr_auto] gap-2 items-center text-xs">
        <span className="font-mono-num font-bold text-[var(--primary)]">{type}</span>
        <span className="font-mono-num">{host}</span>
        <span className="font-mono-num truncate text-[var(--text-secondary)]" title={value}>{value}</span>
        <button
          onClick={onCopy}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md"
          style={{ background: copied ? "var(--accent-terracotta)" : "var(--primary)", color: "#F2EEE7" }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}


const PRESET_DISCOUNTS = [10, 20, 30, 40, 50, 75, 100];

function CouponsManager({ coupons, onCreate, onDeactivate, onPurgeInactive }) {
  const [code, setCode] = useState("");
  const [percentOff, setPercentOff] = useState(20);
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const randomCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // skip 0/O/1/I confusion
    let out = "";
    for (let i = 0; i < 6; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setCode(out);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      code: code.trim().toUpperCase(),
      percent_off: Number(percentOff),
    };
    if (maxRedemptions) payload.max_redemptions = Number(maxRedemptions);
    if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
    if (label.trim()) payload.label = label.trim();
    const ok = await onCreate(payload);
    if (ok) {
      setCode("");
      setMaxRedemptions("");
      setExpiresAt("");
      setLabel("");
    }
    setSubmitting(false);
  };

  const active = coupons.filter((c) => c.active);
  const inactive = coupons.filter((c) => !c.active);

  return (
    <div className="space-y-5" data-testid="admin-coupons">
      <div className="surface-card p-5">
        <h2 className="font-heading font-black text-xl text-[var(--primary)] mb-1">Create a code</h2>
        <p className="text-xs text-[var(--text-secondary)] mb-4">
          Make codes for friends, comps, or full-freebies. 100% means the subscription activates instantly with no card needed.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-uppercase block mb-1.5">Code</label>
            <div className="flex gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. MATE20"
                maxLength={32}
                required
                data-testid="coupon-code-input"
                className="input-field flex-1 font-mono-num uppercase tracking-wider"
                style={{ letterSpacing: "0.12em" }}
              />
              <button
                type="button"
                onClick={randomCode}
                data-testid="coupon-randomize"
                className="text-xs font-heading font-bold px-3 py-2.5 rounded-xl"
                style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
              >
                Random
              </button>
            </div>
            <p className="text-[10px] text-[var(--text-secondary)] mt-1">A–Z and 0–9 only · 3–32 chars · auto-uppercased</p>
          </div>

          <div>
            <label className="label-uppercase block mb-1.5">Discount</label>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="coupon-discount-presets">
              {PRESET_DISCOUNTS.map((p) => {
                const active = Number(percentOff) === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPercentOff(p)}
                    data-testid={`coupon-preset-${p}`}
                    className="text-xs font-heading font-bold px-3 py-1.5 rounded-full transition-colors"
                    style={{
                      background: active ? "var(--primary)" : "var(--accent-sand)",
                      color: active ? "#F2EEE7" : "var(--primary)",
                    }}
                  >
                    {p}%
                    {p === 100 && " 🎁"}
                  </button>
                );
              })}
            </div>
            <input
              type="number"
              min={1}
              max={100}
              value={percentOff}
              onChange={(e) => setPercentOff(e.target.value)}
              data-testid="coupon-percent-input"
              className="input-field font-mono-num"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Max uses</label>
              <input
                type="number"
                min={1}
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                placeholder="Unlimited"
                data-testid="coupon-max-redemptions"
                className="input-field font-mono-num"
              />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Expires</label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="coupon-expires"
                className="input-field font-mono-num"
              />
            </div>
          </div>

          <div>
            <label className="label-uppercase block mb-1.5">Internal note (optional)</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Birthday giveaway"
              maxLength={60}
              data-testid="coupon-label"
              className="input-field"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !code}
            data-testid="coupon-create-submit"
            className="btn-primary w-full"
          >
            {submitting ? "Creating…" : `Create ${percentOff}% code`}
          </button>
        </form>
      </div>

      <div>
        <h2 className="font-heading font-black text-xl text-[var(--primary)] mb-3">
          Active codes <span className="font-mono-num text-sm opacity-60">({active.length})</span>
        </h2>
        {active.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] surface-card-flat p-4" data-testid="coupons-empty">
            No active codes yet. Create one above.
          </p>
        ) : (
          <div className="space-y-2" data-testid="coupons-list">
            {active.map((c) => (
              <CouponRow key={c.id} c={c} onDeactivate={() => onDeactivate(c)} />
            ))}
          </div>
        )}
      </div>

      {inactive.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-[var(--text-secondary)]">
              Deactivated · {inactive.length}
            </h2>
            <button
              type="button"
              onClick={onPurgeInactive}
              data-testid="coupons-purge-inactive"
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/30 transition-colors"
            >
              Delete all
            </button>
          </div>
          <div className="space-y-2">
            {inactive.map((c) => (
              <CouponRow key={c.id} c={c} disabled />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CouponRow({ c, onDeactivate, disabled }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(c.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const used = c.redemptions_count || 0;
  const cap = c.max_redemptions;
  const remaining = cap ? Math.max(0, cap - used) : null;
  const exhausted = cap && remaining === 0;
  const expired = c.expires_at && new Date(c.expires_at) <= new Date();
  return (
    <div
      className={`surface-card-flat p-3 ${disabled ? "opacity-60" : ""}`}
      data-testid={`coupon-row-${c.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={copy}
              data-testid={`coupon-copy-${c.id}`}
              className="font-mono-num font-black text-lg tracking-wider text-[var(--primary)] hover:underline"
              title="Click to copy"
            >
              {c.code}
            </button>
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
              style={{ background: c.percent_off === 100 ? "var(--accent-terracotta)" : "var(--accent-sand)", color: c.percent_off === 100 ? "#F2EEE7" : "var(--primary)" }}
            >
              {c.percent_off === 100 ? "🎁 Free" : `${c.percent_off}% off`}
            </span>
            {copied && <span className="text-[10px] text-[var(--status-good)] font-bold">Copied!</span>}
            {exhausted && <span className="text-[10px] text-[var(--status-bad)] font-bold uppercase">Used up</span>}
            {expired && <span className="text-[10px] text-[var(--status-bad)] font-bold uppercase">Expired</span>}
          </div>
          {c.label && <div className="text-xs text-[var(--text-secondary)] mt-0.5">{c.label}</div>}
          <div className="text-[10px] text-[var(--text-secondary)] mt-1 font-mono-num">
            {used} {used === 1 ? "use" : "uses"}
            {cap ? ` of ${cap}` : " · unlimited"}
            {c.expires_at && ` · expires ${c.expires_at.slice(0, 10)}`}
          </div>
        </div>
        {!disabled && onDeactivate && (
          <button
            type="button"
            onClick={onDeactivate}
            data-testid={`coupon-deactivate-${c.id}`}
            className="text-xs font-bold font-heading px-2.5 py-1.5 rounded-lg flex items-center gap-1 shrink-0"
            style={{ background: "rgba(196,84,60,0.12)", color: "var(--accent-terracotta)" }}
          >
            <Trash size={12} weight="bold" /> Disable
          </button>
        )}
      </div>
    </div>
  );
}


function CourseDataPanel({ onToast, onError }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);
  const [count, setCount] = useState(5);
  const [applyingIds, setApplyingIds] = useState(new Set());
  // Bumped after any apply/revert so the EnrichedCoursesList refreshes.
  const [enrichedReloadKey, setEnrichedReloadKey] = useState(0);
  // When admin taps Edit on an already-enriched row, this opens the full
  // editor modal so they can change tees, par/yards, photo, or course info.
  const [editCourseTarget, setEditCourseTarget] = useState(null);
  // Legacy: when picking a course from the Edit Course modal's "Replace via
  // new photo" button, jump the photo uploader straight to it.
  const [editingCourse, setEditingCourse] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/golfcourseapi/config");
      setCfg(data);
    } catch (err) {
      onError?.(formatApiError(err));
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const saveKey = async () => {
    setSaving(true); onError?.("");
    try {
      const { data } = await api.patch("/admin/golfcourseapi/config", { api_key: draft });
      setCfg(data); setDraft(""); setShowKey(false);
      onToast?.("Key saved");
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Clear the GolfCourseAPI key? Enrichment will stop working until you add a new one.")) return;
    setSaving(true); onError?.("");
    try {
      const { data } = await api.patch("/admin/golfcourseapi/config", { api_key: "" });
      setCfg(data); setDraft("");
      onToast?.("Key cleared");
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true); onError?.("");
    try {
      await api.post("/admin/golfcourseapi/test");
      onToast?.("Connection OK");
      await load();
    } catch (err) {
      onError?.(formatApiError(err));
      await load();
    } finally {
      setTesting(false);
    }
  };

  const runSpotCheck = async () => {
    setChecking(true); onError?.(""); setResults(null);
    try {
      const { data } = await api.post("/admin/golfcourseapi/spot-check", { count });
      setResults(data);
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setChecking(false);
    }
  };

  const applyOne = async (r) => {
    if (!r.matched) return;
    setApplyingIds((s) => new Set([...s, r.course_id]));
    onError?.("");
    try {
      await api.post(`/admin/courses/${r.course_id}/enrich-apply`);
      onToast?.(`Applied: ${r.course_name}`);
      setResults((prev) => prev && {
        ...prev,
        results: prev.results.map((x) =>
          x.course_id === r.course_id ? { ...x, applied: true } : x
        ),
      });
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setApplyingIds((s) => { const n = new Set(s); n.delete(r.course_id); return n; });
    }
  };

  if (!cfg) return <p className="text-[var(--text-secondary)]">Loading...</p>;

  return (
    <div className="space-y-6" data-testid="admin-course-data">
      {/* Country importer — runs the OSM importer against the live DB.
          Lives on top so it's the first thing admin sees after deploy. */}
      <CountryImporterCard onToast={onToast} onError={onError} />


      {/* API key card */}
      <div className="surface-card-flat p-5">
        <h3 className="font-heading font-bold text-[var(--primary)] mb-1">GolfCourseAPI key</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          Get a key at <a href="https://www.golfcourseapi.com/sign-in" target="_blank" rel="noopener noreferrer" className="underline text-[var(--primary)]">golfcourseapi.com</a>. Free tier = 50 requests/day. Each spot-check uses 2 calls (search + detail).
        </p>

        {cfg.has_key ? (
          <div className="flex items-center justify-between rounded-xl bg-[var(--bg-app)] px-3 py-2 mb-3" data-testid="api-key-current">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldCheck size={16} weight="fill" className="shrink-0 text-[var(--primary)]" />
              <span className="font-mono-num text-sm truncate">{cfg.key_masked}</span>
              <span className="text-[10px] uppercase tracking-wider opacity-60 shrink-0">saved {cfg.updated_at?.slice(0, 10)}</span>
            </div>
            <button
              onClick={clearKey}
              disabled={saving}
              className="text-xs font-bold text-[var(--accent-terracotta)] hover:underline ml-2 shrink-0"
              data-testid="api-key-clear"
            >
              Clear
            </button>
          </div>
        ) : (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
            No key set yet — paste one below.
          </div>
        )}

        <div className="flex gap-2">
          <input
            type={showKey ? "text" : "password"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={cfg.has_key ? "Paste a new key to rotate..." : "Paste your API key"}
            className="input-field flex-1 font-mono-num text-sm"
            data-testid="api-key-input"
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            className="text-xs font-bold text-[var(--text-secondary)] px-3"
            title={showKey ? "Hide" : "Show"}
          >
            {showKey ? "Hide" : "Show"}
          </button>
          <button
            onClick={saveKey}
            disabled={saving || !draft.trim()}
            data-testid="api-key-save"
            className="btn-primary px-4 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>

        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[var(--border-default)]">
          <button
            onClick={testConnection}
            disabled={testing || !cfg.has_key}
            data-testid="api-key-test"
            className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-xl"
            style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
          >
            {testing ? "Pinging..." : "Test connection"}
          </button>
          {cfg.last_tested_at && (
            <span className="text-xs flex items-center gap-1.5">
              {cfg.last_test_ok ? (
                <CheckCircle size={14} weight="fill" className="text-green-700" />
              ) : (
                <X size={14} weight="bold" className="text-[var(--accent-terracotta)]" />
              )}
              <span className={cfg.last_test_ok ? "text-green-700" : "text-[var(--accent-terracotta)]"}>
                {cfg.last_test_message}
              </span>
              <span className="text-[10px] opacity-50">· {cfg.last_tested_at.slice(11, 16)} {cfg.last_tested_at.slice(0, 10)}</span>
            </span>
          )}
        </div>
      </div>

      {/* Photo uploader card — admin extracts a scorecard from a photo and writes directly */}
      <ScorecardPhotoUploader
        onToast={onToast}
        onError={onError}
        onApplied={() => setEnrichedReloadKey((k) => k + 1)}
        externalPicked={editingCourse}
        onClearExternalPick={() => setEditingCourse(null)}
      />

      {/* Edit Course modal — opens from the Edit button on Recently Enriched */}
      {editCourseTarget && (
        <EditCourseModal
          courseId={editCourseTarget.id}
          onClose={() => setEditCourseTarget(null)}
          onSaved={() => {
            setEnrichedReloadKey((k) => k + 1);
            onToast?.(`Saved: ${editCourseTarget.name}`);
            setEditCourseTarget(null);
          }}
          onError={onError}
        />
      )}

      {/* Pending user submissions — review + approve/reject before publish */}
      <PendingScorecardSubmissions
        onToast={onToast}
        onError={onError}
        onChanged={() => setEnrichedReloadKey((k) => k + 1)}
      />

      {/* Recently enriched — audit / edit / revert */}
      <EnrichedCoursesList
        onToast={onToast}
        onError={onError}
        reloadKey={enrichedReloadKey}
        onChanged={() => setEnrichedReloadKey((k) => k + 1)}
        onEdit={(c) => setEditCourseTarget(c)}
      />

      {/* Spot-check card */}
      <div className="surface-card-flat p-5">
        <h3 className="font-heading font-bold text-[var(--primary)] mb-1">Spot-check coverage</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          Picks random placeholder courses from your DB, searches GolfCourseAPI by name, returns matches without writing anything. Use this to see how good the coverage is BEFORE running a bulk enrichment.
        </p>
        <div className="flex items-center gap-2 mb-3">
          <label className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Sample size</label>
          <input
            type="number"
            min={1}
            max={25}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(25, Number(e.target.value) || 1)))}
            className="input-field w-20 text-center"
            data-testid="spot-check-count"
          />
          <span className="text-xs text-[var(--text-secondary)]">= {count * 2} API calls</span>
          <button
            onClick={runSpotCheck}
            disabled={checking || !cfg.has_key}
            data-testid="spot-check-run"
            className="btn-primary ml-auto disabled:opacity-50"
          >
            {checking ? "Running..." : "Run spot-check"}
          </button>
        </div>

        {results && (
          <div data-testid="spot-check-results">
            <div className="rounded-xl bg-[var(--bg-app)] px-3 py-2 mb-3 text-sm flex items-center justify-between">
              <span>
                <strong>{results.matched}/{results.tested}</strong> matched
              </span>
              <span className="font-mono-num text-[var(--primary)] font-bold">
                {Math.round(results.match_rate * 100)}% hit rate
              </span>
            </div>
            <div className="space-y-2">
              {results.results.map((r, i) => (
                <div
                  key={r.course_id || r.name || i}
                  className={`rounded-xl px-3 py-2.5 text-sm border ${
                    r.matched
                      ? "bg-green-50 border-green-200"
                      : "bg-red-50/50 border-red-200/70"
                  }`}
                  data-testid={`spot-check-row-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[var(--primary)] truncate">{r.course_name}</div>
                      {r.matched ? (
                        <>
                          <div className="text-[11px] text-[var(--text-secondary)] truncate">
                            → {r.match.club_name} · {r.match.location?.country || "?"}
                          </div>
                          <div className="text-[11px] font-mono-num mt-1 text-green-800">
                            Par {r.match.par_total} · {r.match.yards_total} yds · {r.match.tee_name} tee · CR {r.match.course_rating}/Slope {r.match.slope_rating}
                          </div>
                        </>
                      ) : (
                        <div className="text-[11px] text-[var(--accent-terracotta)] italic">{r.error}</div>
                      )}
                    </div>
                    {r.matched && (
                      r.applied ? (
                        <span className="text-xs font-bold text-green-700 px-2 py-1.5 inline-flex items-center gap-1 shrink-0">
                          <CheckCircle size={14} weight="fill" /> Applied
                        </span>
                      ) : (
                        <button
                          onClick={() => applyOne(r)}
                          disabled={applyingIds.has(r.course_id)}
                          data-testid={`apply-enrichment-${r.course_id}`}
                          className="text-xs font-bold font-heading px-3 py-1.5 rounded-lg shrink-0 disabled:opacity-50"
                          style={{ background: "var(--primary)", color: "#F2EEE7" }}
                        >
                          {applyingIds.has(r.course_id) ? "Applying..." : "Apply"}
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!results && !checking && (
          <p className="text-xs text-[var(--text-secondary)] italic mt-2">
            Tip — UK coverage on GolfCourseAPI&apos;s free tier is patchy (heavily US/Canada-skewed). If you see &lt;30% hit rate here, you&apos;ll want Golf API UK at $600/yr or stick with the photo + crowdsource flow.
          </p>
        )}
      </div>
    </div>
  );
}

function ScorecardPhotoUploader({ onToast, onError, onApplied, externalPicked, onClearExternalPick }) {
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  // Lets the admin manually override the tee colour Gemini detected
  // (in case Gemini reads "Yellow" but the photo is actually White, etc).
  const [teeOverride, setTeeOverride] = useState("");
  // Multi-tee: which extra tee colours from the scorecard to also apply.
  // Each entry: { label, holes: [{number, yards}] }. The "primary" tee
  // (teeOverride || extracted.tee_label) is NOT in this list.
  const [extraTees, setExtraTees] = useState([]);

  // When the "Edit" button on the EnrichedCoursesList passes a course, jump
  // straight into the upload step for that course.
  useEffect(() => {
    if (externalPicked) {
      setPicked(externalPicked);
      setExtracted(null);
      setPreview(null);
      setSearch("");
      setMatches([]);
    }
  }, [externalPicked]);

  useEffect(() => {
    if (!search || search.length < 2) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/courses/placeholder-list?q=${encodeURIComponent(search)}&limit=20`);
        setMatches(data?.courses || []);
      } catch (err) {
        onError?.(formatApiError(err));
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, onError]);

  const reset = () => {
    setPicked(null); setExtracted(null); setPreview(null);
    setSearch(""); setMatches([]); setTeeOverride(""); setExtraTees([]);
    onClearExternalPick?.();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !picked) return;
    setExtracting(true); setExtracted(null); onError?.("");
    setTeeOverride("");
    setExtraTees([]);
    setPreview(URL.createObjectURL(file));
    try {
      const form = new FormData();
      form.append("image", file);
      const { data } = await api.post(`/courses/${picked.id}/extract-scorecard`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtracted(data);
      // Auto-tick every extra tee Gemini found (everything other than the
      // primary/longest tee). Each entry mirrors the per-hole layout so the
      // preview table can render one yards column per tee.
      const primary = (data.tee_label || "").toLowerCase();
      const extras = (Array.isArray(data.tees) ? data.tees : [])
        .filter((t) => t.label && t.label.toLowerCase() !== primary)
        .map((t) => ({
          label: t.label.toLowerCase(),
          holes: (data.holes || []).map((h) => {
            const match = (t.holes || []).find((x) => x.number === h.number);
            return { number: h.number, yards: match?.yards ?? h.yards };
          }),
        }));
      setExtraTees(extras);
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setExtracting(false);
    }
  };

  const apply = async () => {
    if (!picked || !extracted) return;
    setApplying(true); onError?.("");
    try {
      const primaryLabel = teeOverride || extracted.tee_label || "default";
      // Drop any extra tee that collides with the chosen primary so we
      // don't double-apply the same colour.
      const cleanedExtras = extraTees.filter((t) => t.label !== primaryLabel);
      await api.post(`/admin/courses/${picked.id}/apply-stats`, {
        par_total: extracted.par_total,
        yards_total: extracted.yards_total,
        holes: (extracted.holes || []).map((h) => ({
          number: h.number,
          par: h.par,
          yards: h.yards,
          stroke_index: h.stroke_index ?? null,
        })),
        tee_label: primaryLabel,
        tees: cleanedExtras.length > 0
          ? cleanedExtras.map((t) => ({ label: t.label, holes: t.holes }))
          : null,
      });
      const allTees = [primaryLabel, ...cleanedExtras.map((t) => t.label)];
      onToast?.(
        allTees.length > 1
          ? `Applied: ${picked.name} (${allTees.length} tees: ${allTees.join(", ")})`
          : `Applied: ${picked.name} (${primaryLabel} tee)`
      );
      onApplied?.();
      reset();
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="surface-card-flat p-5" data-testid="admin-scorecard-uploader">
      <h3 className="font-heading font-bold text-[var(--primary)] mb-1">Upload a scorecard photo</h3>
      <p className="text-xs text-[var(--text-secondary)] mb-3">
        Pick a placeholder course, snap or upload a photo of its real scorecard, then apply. Bypasses the moderation queue (you&apos;re admin). Reuses the same Gemini extractor as the public submit flow.
      </p>

      {!picked ? (
        <div data-testid="uploader-step-pick">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search placeholder courses by name..."
            className="input-field w-full"
            data-testid="uploader-course-search"
          />
          {matches.length > 0 && (
            <div className="mt-2 max-h-64 overflow-y-auto space-y-1" data-testid="uploader-course-matches">
              {matches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setPicked(c)}
                  data-testid={`uploader-pick-${c.id}`}
                  className="w-full text-left rounded-xl px-3 py-2 text-sm bg-[var(--bg-app)] hover:bg-[var(--accent-sand)] transition-colors"
                >
                  <div className="font-bold text-[var(--primary)]">{c.name}</div>
                  <div className="text-[11px] text-[var(--text-secondary)] truncate">{c.location || ""}</div>
                </button>
              ))}
            </div>
          )}
          {search.length >= 2 && matches.length === 0 && (
            <p className="text-xs text-[var(--text-secondary)] mt-2 italic">No placeholder courses match — they may already have real data.</p>
          )}
        </div>
      ) : (
        <div data-testid="uploader-step-photo">
          <div className="flex items-center justify-between rounded-xl bg-[var(--bg-app)] px-3 py-2 mb-3">
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm text-[var(--primary)] truncate">{picked.name}</div>
              <div className="text-[11px] text-[var(--text-secondary)] truncate">{picked.location || ""}</div>
            </div>
            <button
              type="button"
              onClick={reset}
              className="text-xs font-bold text-[var(--text-secondary)] hover:underline ml-2 shrink-0"
              data-testid="uploader-reset"
            >
              Change
            </button>
          </div>

          {!extracted && (
            <div className="grid grid-cols-2 gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={onFile} disabled={extracting} data-testid="uploader-camera" />
                <span className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider ${extracting ? "opacity-50" : ""}`}>
                  {extracting ? "Reading…" : "Take photo"}
                </span>
              </label>
              <label className="cursor-pointer">
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} disabled={extracting} data-testid="uploader-upload" />
                <span className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full border-2 border-[var(--primary)] text-[var(--primary)] bg-white text-xs font-bold uppercase tracking-wider ${extracting ? "opacity-50" : ""}`}>
                  Upload
                </span>
              </label>
            </div>
          )}

          {preview && (
            <img src={preview} alt="" className="mt-3 max-h-48 rounded-xl border border-[var(--border-default)] object-contain w-full" />
          )}

          {extracted && (
            <div className="mt-3" data-testid="uploader-extracted">
              <div className="rounded-xl bg-green-50 border border-green-200 px-3 py-2 mb-3 text-sm">
                <div className="font-bold text-green-900 mb-1">
                  ✓ Par {extracted.par_total} · {extracted.yards_total} yds · {extracted.holes?.length} holes
                  {extraTees.length > 0 && (
                    <span className="ml-2 text-[11px] font-normal opacity-80">
                      + {extraTees.length} extra tee{extraTees.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-green-900">Primary tee:</label>
                  <select
                    value={teeOverride || extracted.tee_label || "default"}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Don't let primary collide with an extra tee.
                      setExtraTees((arr) => arr.filter((t) => t.label !== v));
                      setTeeOverride(v);
                    }}
                    data-testid="uploader-tee-select"
                    className="text-xs px-2 py-1 rounded border border-green-300 bg-white"
                  >
                    <option value="blue">Blue</option>
                    <option value="white">White</option>
                    <option value="yellow">Yellow</option>
                    <option value="red">Red</option>
                    <option value="black">Black</option>
                    <option value="gold">Gold</option>
                    <option value="green">Green</option>
                    <option value="default">Default / unmarked</option>
                  </select>
                  {extracted.tee_label && (
                    <span className="text-[10px] italic text-green-700">(Gemini detected: {extracted.tee_label})</span>
                  )}
                </div>
                {/* Multi-tee chips — every other tee colour Gemini found can
                    be ticked to be applied in the same call. */}
                <div className="mb-1" data-testid="uploader-extra-tees">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-green-900 mb-1">
                    Other tees on this card
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: "blue",   label: "Blue",   swatch: "#3B82F6", textOnSwatch: "#fff" },
                      { id: "white",  label: "White",  swatch: "#ffffff", textOnSwatch: "#143D2F" },
                      { id: "yellow", label: "Yellow", swatch: "#FFE45E", textOnSwatch: "#143D2F" },
                      { id: "red",    label: "Red",    swatch: "#ef4444", textOnSwatch: "#fff" },
                      { id: "black",  label: "Black",  swatch: "#111111", textOnSwatch: "#fff" },
                      { id: "gold",   label: "Gold",   swatch: "#D4AF37", textOnSwatch: "#143D2F" },
                      { id: "green",  label: "Green",  swatch: "#16a34a", textOnSwatch: "#fff" },
                    ].map((opt) => {
                      const primary = (teeOverride || extracted.tee_label || "default").toLowerCase();
                      const isPrimary = primary === opt.id;
                      const idx = extraTees.findIndex((t) => t.label === opt.id);
                      const isExtra = idx >= 0;
                      const aiHit = (extracted.tees || []).find((t) => (t.label || "").toLowerCase() === opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          disabled={isPrimary}
                          title={isPrimary ? "Already set as the primary tee" : (aiHit ? "Detected by Gemini" : "Add this tee with default 380 yds per hole")}
                          onClick={() => {
                            if (isPrimary) return;
                            if (isExtra) {
                              setExtraTees((arr) => arr.filter((_, i) => i !== idx));
                            } else {
                              setExtraTees((arr) => [
                                ...arr,
                                {
                                  label: opt.id,
                                  holes: (extracted.holes || []).map((h) => {
                                    const m = aiHit?.holes?.find((x) => x.number === h.number);
                                    return { number: h.number, yards: m?.yards ?? h.yards };
                                  }),
                                },
                              ]);
                            }
                          }}
                          data-testid={`uploader-extra-tee-${opt.id}`}
                          className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold border transition-all ${
                            isExtra
                              ? "border-green-600 bg-green-100 text-green-900"
                              : "border-green-300 bg-white text-green-900"
                          } ${isPrimary ? "opacity-40" : ""}`}
                        >
                          <span
                            className="h-2.5 w-2.5 rounded-full border border-black/20 shrink-0"
                            style={{ background: opt.swatch }}
                          />
                          {opt.label}
                          {aiHit && !isPrimary && (
                            <span className="text-[9px] opacity-70">✓AI</span>
                          )}
                          {isPrimary && <span className="text-[9px] opacity-70">primary</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="text-[11px] text-green-800 mt-1">
                  Confidence: <strong>{extracted.confidence}</strong>{extracted.notes ? ` · ${extracted.notes}` : ""}
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto rounded-xl border border-[var(--border-default)]" data-testid="uploader-preview-table">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--secondary)] text-[var(--primary)]">
                    <tr>
                      <th className="py-1 px-3 text-left">#</th>
                      <th className="py-1 px-3">Par</th>
                      <th className="py-1 px-3 text-right">
                        Yds<div className="text-[8px] font-normal opacity-70 normal-case">{teeOverride || extracted.tee_label || "main"}</div>
                      </th>
                      {extraTees.map((t) => (
                        <th key={t.label} className="py-1 px-3 text-right">
                          Yds<div className="text-[8px] font-normal opacity-70 normal-case">{t.label}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(extracted.holes || []).map((h) => (
                      <tr key={h.number} className="border-t border-[var(--border-default)]">
                        <td className="py-1 px-3 font-mono-num font-bold text-[var(--primary)]">{h.number}</td>
                        <td className="py-1 px-3 font-mono-num text-center">{h.par}</td>
                        <td className="py-1 px-3 font-mono-num text-right">{h.yards}</td>
                        {extraTees.map((t) => {
                          const cell = t.holes.find((x) => x.number === h.number);
                          return (
                            <td key={t.label} className="py-1 px-3 font-mono-num text-right text-[var(--text-secondary)]">
                              {cell?.yards ?? "—"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => { setExtracted(null); setPreview(null); }}
                  className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-xl border border-[var(--border-default)]"
                  data-testid="uploader-retry"
                >
                  Retry photo
                </button>
                <button
                  type="button"
                  onClick={apply}
                  disabled={applying}
                  className="btn-primary ml-auto disabled:opacity-50"
                  data-testid="uploader-apply"
                >
                  {applying ? "Applying…" : "Apply to course"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function BackupsPanel({ onToast, onError }) {
  const [summary, setSummary] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, snaps] = await Promise.all([
        api.get("/admin/backups/summary"),
        api.get("/admin/backups/snapshots"),
      ]);
      setSummary(s.data);
      setSnapshots(snaps.data?.snapshots || []);
    } catch (err) {
      onError?.(formatApiError(err));
    }
  }, [onError]);

  useEffect(() => { refresh(); }, [refresh]);

  const download = async () => {
    setDownloading(true); onError?.("");
    try {
      const resp = await api.post("/admin/backups/export", null, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `fairway-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onToast?.("Backup downloaded");
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  const restoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm(`⚠ Restore the entire database from "${file.name}"?\n\nThis OVERWRITES all current users, courses, rounds, leagues and config. A snapshot of the CURRENT state is taken automatically so you can roll back.`)) return;
    const typed = window.prompt("Final check: type RESTORE (in capitals) to apply.");
    if (typed !== "RESTORE") { onToast?.("Cancelled."); return; }
    setRestoring(true); onError?.("");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("confirm", "RESTORE");
      const { data } = await api.post("/admin/backups/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      onToast?.(`Restored ${total} docs across ${Object.keys(data.restored || {}).length} collections.`);
      await refresh();
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setRestoring(false);
    }
  };

  const restoreSnapshot = async (snap) => {
    if (!window.confirm(`Restore from snapshot taken at ${snap.created_at?.replace("T", " ").slice(0, 16)} (${snap.total_docs} docs)?\n\nThis OVERWRITES the current DB. Another snapshot of the current state will be taken first.`)) return;
    const typed = window.prompt("Final check: type RESTORE (in capitals) to apply.");
    if (typed !== "RESTORE") { onToast?.("Cancelled."); return; }
    setRestoring(true); onError?.("");
    try {
      const { data } = await api.post(`/admin/backups/snapshots/${snap.id}/restore`, { confirm: "RESTORE" });
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      onToast?.(`Restored ${total} docs from snapshot.`);
      await refresh();
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setRestoring(false);
    }
  };

  if (!summary) return <p className="text-[var(--text-secondary)]">Loading...</p>;

  return (
    <div className="space-y-6" data-testid="admin-backups">
      {/* Download card */}
      <div className="surface-card-flat p-5">
        <h3 className="font-heading font-bold text-[var(--primary)] mb-1">Download a backup</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          Saves every user, course, round, league, coupon, payment + config to a single JSON file. Keep this somewhere safe — you can re-upload it to undo any catastrophic change. <strong>Tip:</strong> click this before any bulk admin action.
        </p>

        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          {Object.entries(summary.collections).map(([name, count]) => (
            <div key={name} className="rounded-lg bg-[var(--bg-app)] px-3 py-2 flex justify-between" data-testid={`backup-count-${name}`}>
              <span className="text-[var(--text-secondary)]">{name}</span>
              <span className="font-mono-num font-bold text-[var(--primary)]">{count}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-[var(--text-secondary)]">
            <strong className="text-[var(--primary)] font-mono-num">{summary.total_docs}</strong> total docs
          </span>
          <button
            onClick={download}
            disabled={downloading}
            data-testid="backup-download"
            className="btn-primary disabled:opacity-50"
          >
            {downloading ? "Saving..." : "⬇ Download backup"}
          </button>
        </div>
      </div>

      {/* Restore from file card */}
      <div className="surface-card-flat p-5">
        <h3 className="font-heading font-bold text-[var(--primary)] mb-1">Restore from a backup file</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          Pick a previously-downloaded <code className="px-1 rounded bg-[var(--bg-app)]">fairway-backup-*.json</code> file. Requires typing RESTORE to confirm — and auto-snapshots the current state before applying so the restore is itself reversible.
        </p>
        <label className="cursor-pointer inline-block">
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            disabled={restoring}
            onChange={restoreFile}
            data-testid="backup-restore-file"
          />
          <span
            className={`btn-primary inline-flex items-center gap-2 ${restoring ? "opacity-50" : ""}`}
            style={{ background: "var(--accent-terracotta)" }}
            data-testid="backup-restore-trigger"
          >
            {restoring ? "Restoring..." : "⬆ Restore from file"}
          </span>
        </label>
      </div>

      {/* Auto-snapshots card */}
      <div className="surface-card-flat p-5">
        <h3 className="font-heading font-bold text-[var(--primary)] mb-1">Auto-snapshots (server-side)</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          Captured automatically before any destructive admin action (Purge users, Restore, etc.). The last 10 are kept. Use this to roll back even if you don&apos;t have the file.
        </p>
        {snapshots.length === 0 ? (
          <p className="text-xs italic text-[var(--text-secondary)]">No snapshots yet — they&apos;ll appear here next time a destructive action runs.</p>
        ) : (
          <div className="space-y-2" data-testid="backup-snapshots-list">
            {snapshots.map((s) => (
              <div key={s.id} className="rounded-xl bg-[var(--bg-app)] px-3 py-2.5 flex items-center justify-between" data-testid={`backup-snapshot-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-num text-sm font-bold text-[var(--primary)]">
                    {s.created_at?.slice(0, 10)} · {s.created_at?.slice(11, 16)}
                  </div>
                  <div className="text-[11px] text-[var(--text-secondary)] truncate">
                    {s.triggered_by} · {s.total_docs} docs ({Object.entries(s.counts || {}).filter(([_, n]) => n > 0).map(([k, n]) => `${k}:${n}`).join(", ").slice(0, 100)}…)
                  </div>
                </div>
                <button
                  onClick={() => restoreSnapshot(s)}
                  disabled={restoring}
                  className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg disabled:opacity-50"
                  style={{ background: "var(--accent-terracotta)", color: "white" }}
                  data-testid={`backup-snapshot-restore-${s.id}`}
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/*
  PendingScorecardSubmissions — admin review queue for user-uploaded
  scorecards. Each row shows the submitter, the course, the extracted par
  + yards, per-tee breakdown (if multiple tees on one photo), OCR
  confidence, and Approve / Reject buttons.

  Backend endpoints already exist:
    GET  /api/admin/course-submissions?status=pending
    POST /api/admin/course-submissions/{id}/approve
    POST /api/admin/course-submissions/{id}/reject

  `onChanged` bubbles up so parent bumps the "Recently enriched" list
  reload key — approved submissions immediately appear in the audit
  section below without a manual refresh.
*/
function PendingScorecardSubmissions({ onToast, onError, onChanged }) {
  const [rows, setRows] = useState(null);
  const [actioning, setActioning] = useState(null); // submission id

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/course-submissions?status=pending");
      setRows(data?.submissions || []);
    } catch (err) {
      onError?.(formatApiError(err));
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const approve = async (s) => {
    setActioning(s.id); onError?.("");
    try {
      const { data } = await api.post(`/admin/course-submissions/${s.id}/approve`);
      onToast?.(`Approved · ${s.course_name} (${(data?.tees_applied || []).join(", ") || "totals only"})`);
      setRows((r) => (r || []).filter((x) => x.id !== s.id));
      onChanged?.();
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setActioning(null);
    }
  };

  const reject = async (s) => {
    if (!window.confirm(`Reject "${s.course_name}" submission from ${s.user_email || s.user_name || "user"}?`)) return;
    setActioning(s.id); onError?.("");
    try {
      await api.post(`/admin/course-submissions/${s.id}/reject`);
      onToast?.(`Rejected · ${s.course_name}`);
      setRows((r) => (r || []).filter((x) => x.id !== s.id));
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setActioning(null);
    }
  };

  const confidenceBadge = (c) => {
    const cfg = {
      high:   { label: "OCR HIGH",   bg: "rgba(16,185,129,.18)", fg: "#0F9C6C" },
      medium: { label: "OCR MEDIUM", bg: "rgba(217,119,6,.18)",  fg: "#B45309" },
      low:    { label: "OCR LOW",    bg: "rgba(196,84,60,.18)",  fg: "#B04A34" },
    }[String(c || "").toLowerCase()];
    if (!cfg) return null;
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.fg }}>
        {cfg.label}
      </span>
    );
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  if (rows === null) return null;

  return (
    <div className="surface-card-flat p-5 mb-4" data-testid="admin-pending-submissions">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h3 className="font-heading font-bold text-[var(--primary)]">Pending scorecard submissions</h3>
          {rows.length > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full text-[11px] font-black text-white px-1.5"
              style={{ background: "var(--accent-terracotta)" }}
              data-testid="pending-badge-count"
            >
              {rows.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)] hover:text-[var(--primary)]"
          data-testid="pending-refresh"
        >
          Refresh
        </button>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-3">
        Users upload scorecard photos → Gemini reads them → medium/low-confidence rows land here for you to eyeball before publishing. High-confidence rows auto-approve.
      </p>

      {rows.length === 0 ? (
        <p className="text-xs italic text-[var(--text-secondary)]" data-testid="pending-empty">
          No submissions waiting. Great work — the queue&apos;s clear!
        </p>
      ) : (
        <div className="space-y-2" data-testid="pending-rows">
          {rows.map((s) => {
            const isBusy = actioning === s.id;
            const teesRead = (s.tees || []).map((t) => (t.label || "").toLowerCase()).filter(Boolean);
            return (
              <div
                key={s.id}
                className="rounded-xl border border-[var(--border-default)] bg-white p-3"
                data-testid={`pending-row-${s.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-sm text-[var(--primary)] truncate" data-testid={`pending-row-course-${s.id}`}>
                        {s.course_name || "(untitled course)"}
                      </span>
                      {confidenceBadge(s.ocr_confidence)}
                    </div>
                    <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                      {s.user_name || s.user_email || "user"}
                      {s.user_email && s.user_name ? ` · ${s.user_email}` : ""}
                      {" · "}
                      <span className="font-mono-num">{fmtDate(s.submitted_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-2">
                  <MiniStat label="Par" value={s.par_total ?? "—"} />
                  <MiniStat label="Yards" value={s.yards_total ?? "—"} />
                  <MiniStat
                    label="Holes"
                    value={
                      Array.isArray(s.holes) && s.holes.length
                        ? s.holes.length
                        : "—"
                    }
                  />
                  <MiniStat
                    label="Tees"
                    value={teesRead.length || (s.tee_label ? 1 : 0) || "—"}
                    title={teesRead.length ? teesRead.join(", ") : (s.tee_label || "")}
                  />
                </div>

                {teesRead.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3" data-testid={`pending-tees-${s.id}`}>
                    {teesRead.map((tc) => (
                      <span
                        key={tc}
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={teeHeaderStyle(tc)}
                      >
                        {tc}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => approve(s)}
                    disabled={isBusy}
                    data-testid={`pending-approve-${s.id}`}
                    className="flex-1 rounded-full py-2 text-xs font-heading font-bold uppercase tracking-wider transition-colors"
                    style={{ background: "var(--primary)", color: "#F2EEE7", opacity: isBusy ? 0.6 : 1 }}
                  >
                    {isBusy ? "Working…" : "Approve"}
                  </button>
                  <button
                    type="button"
                    onClick={() => reject(s)}
                    disabled={isBusy}
                    data-testid={`pending-reject-${s.id}`}
                    className="flex-1 rounded-full py-2 text-xs font-heading font-bold uppercase tracking-wider border-2 transition-colors"
                    style={{ borderColor: "var(--accent-terracotta)", color: "var(--accent-terracotta)", opacity: isBusy ? 0.6 : 1 }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, title }) {
  return (
    <div
      className="rounded-lg bg-[var(--bg-app)] px-2 py-1 text-center"
      title={title || ""}
    >
      <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">{label}</div>
      <div className="font-mono-num text-sm font-black text-[var(--primary)] leading-tight">{value}</div>
    </div>
  );
}

// Standard UK scorecard tee-colour palette. Shared with the pending queue
// and the course-detail Scorecard grid.
function teeHeaderStyle(tc) {
  const map = {
    blue:   { background: "#3B82F6", color: "#F2EEE7" },
    red:    { background: "#EF4444", color: "#F2EEE7" },
    white:  { background: "#F2EEE7", color: "#111" },
    yellow: { background: "#FFE45E", color: "#111" },
    black:  { background: "#111",    color: "#F2EEE7" },
    gold:   { background: "#D4AF37", color: "#111" },
    green:  { background: "#16a34a", color: "#F2EEE7" },
  };
  return map[tc] || { background: "var(--bg-app)", color: "var(--text)" };
}




function EnrichedCoursesList({ onToast, onError, reloadKey, onChanged, onEdit }) {
  const [rows, setRows] = useState(null);
  const [reverting, setReverting] = useState(null); // course_id while in-flight
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/courses/enriched?limit=200");
      setRows(data?.courses || []);
    } catch (err) {
      onError?.(formatApiError(err));
    }
  }, [onError]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const revert = async (c) => {
    if (!window.confirm(`Delete the scorecard data for "${c.name}"?\n\nThe course goes back to placeholder (Estimated) until you re-upload. GPS pin positions stay intact.`)) return;
    setReverting(c.id); onError?.("");
    try {
      await api.post(`/admin/courses/${c.id}/revert-enrichment`);
      onToast?.(`Reverted: ${c.name}`);
      onChanged?.();
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setReverting(null);
    }
  };

  const sourceBadge = (src) => {
    const map = {
      photo_extract: { label: "📸 Photo", style: { background: "var(--accent-sand)", color: "var(--primary)" } },
      golfcourseapi: { label: "🌐 API", style: { background: "rgba(20,61,47,0.1)", color: "var(--primary)" } },
    };
    const cfg = map[src] || { label: src || "—", style: { background: "var(--bg-app)", color: "var(--text-secondary)" } };
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={cfg.style}>
        {cfg.label}
      </span>
    );
  };

  if (rows === null) return null;

  return (
    <div className="surface-card-flat p-5" data-testid="admin-enriched-list">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading font-bold text-[var(--primary)]">Recently enriched courses</h3>
        <span className="text-xs text-[var(--text-secondary)] font-mono-num">{rows.length}</span>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-3">
        Every course you&apos;ve added a real scorecard to. <strong>Edit</strong> re-opens the upload flow so you can overwrite with a new photo; <strong>Delete</strong> reverts the course back to placeholder.
      </p>

      {rows.length === 0 ? (
        <p className="text-xs italic text-[var(--text-secondary)]">
          Nothing here yet — upload a scorecard photo above and it&apos;ll appear in this list.
        </p>
      ) : (
        <div className="space-y-2" data-testid="enriched-rows">
          {rows.map((c) => (
            <div
              key={c.id}
              className="rounded-xl px-3 py-3 bg-[var(--bg-app)] flex items-start justify-between gap-2"
              data-testid={`enriched-row-${c.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-heading font-bold text-sm text-[var(--primary)] truncate">{c.name}</span>
                  {sourceBadge(c.external_source)}
                </div>
                <div className="text-[11px] text-[var(--text-secondary)] truncate">
                  {c.location || ""}
                </div>
                <div className="text-[11px] font-mono-num mt-1 text-[var(--primary)]">
                  Par {c.par_total} · {c.yards_total} yds · {c.hole_count} holes
                  {c.course_rating ? ` · CR ${c.course_rating}` : ""}
                  {c.slope_rating ? ` / S${c.slope_rating}` : ""}
                </div>
                {/* Tee chips — one per saved tee colour. */}
                {(c.tees_available || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {c.tees_available.map((tee) => (
                      <span
                        key={tee}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{
                          background: ({
                            blue: "#1e40af", white: "#e5e7eb", yellow: "#facc15",
                            red: "#dc2626", black: "#0f172a", gold: "#ca8a04", green: "#15803d",
                          })[tee] || "var(--accent-sand)",
                          color: ({
                            white: "#0f172a", yellow: "#0f172a", gold: "#0f172a",
                          })[tee] || "#fff",
                        }}
                        data-testid={`tee-chip-${c.id}-${tee}`}
                      >
                        {tee} ✓
                      </span>
                    ))}
                  </div>
                )}
                {c.enriched_at && (
                  <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                    Saved {c.enriched_at.slice(0, 10)} · {c.enriched_at.slice(11, 16)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <a
                  href={`/app/courses/${c.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg text-center"
                  style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
                  data-testid={`enriched-preview-${c.id}`}
                  title="Open the live course page the way users see it"
                >
                  Preview
                </a>
                <button
                  type="button"
                  onClick={() => onEdit?.(c)}
                  className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg"
                  style={{ background: "var(--primary)", color: "white" }}
                  data-testid={`enriched-edit-${c.id}`}
                  title="Re-upload a new scorecard photo for this course"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => revert(c)}
                  disabled={reverting === c.id}
                  className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg disabled:opacity-50"
                  style={{ background: "rgba(196,84,60,0.12)", color: "var(--accent-terracotta)", border: "1px solid rgba(196,84,60,0.3)" }}
                  data-testid={`enriched-delete-${c.id}`}
                  title="Revert back to placeholder so you can re-upload from scratch"
                >
                  {reverting === c.id ? "Reverting…" : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// Standard UK tee palette used in the editor for adding/labelling tee columns.
const TEE_PALETTE = [
  { id: "blue",   label: "Blue",   swatch: "#3B82F6" },
  { id: "white",  label: "White",  swatch: "#ffffff" },
  { id: "yellow", label: "Yellow", swatch: "#FFE45E" },
  { id: "red",    label: "Red",    swatch: "#ef4444" },
  { id: "black",  label: "Black",  swatch: "#111111" },
  { id: "gold",   label: "Gold",   swatch: "#D4AF37" },
  { id: "green",  label: "Green",  swatch: "#16a34a" },
  { id: "silver", label: "Silver", swatch: "#C0C0C0" },
];

/**
 * EditCourseModal — full-blown course editor opened from the Edit button.
 * Tabs: Info (name/location/description), Photo (replace), Holes & Tees
 * (par/yards + per-tee yardages, add/remove tee colours).
 */
function EditCourseModal({ courseId, onClose, onSaved, onError }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("info"); // info | photo | holes
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [imageDirty, setImageDirty] = useState(false);
  // holes: [{number, par, yards (default/longest), tees:[{color,yards}]}]
  const [holes, setHoles] = useState([]);
  const [teeCols, setTeeCols] = useState([]); // ordered list of tee colours visible in the editor
  // Track whether the user changed holes/info so we PATCH only what's needed.
  const [holesDirty, setHolesDirty] = useState(false);
  // Re-extract: snap a new card → Gemini Vision → loads back into holes editor.
  const [reExtracting, setReExtracting] = useState(false);
  const [reExtractMsg, setReExtractMsg] = useState("");
  const fileInputRef = useRef(null);
  const reExtractFileRef = useRef(null);

  // Load the full course on open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/courses/${courseId}/full`);
        if (cancelled) return;
        setName(data.name || "");
        setLocation(data.location || "");
        setDescription(data.description || "");
        setImageUrl(data.image_url || null);
        setImageDirty(false);
        const initialHoles = (data.holes || []).map((h) => ({
          number: h.number,
          par: h.par,
          yards: h.yards,
          stroke_index: h.stroke_index ?? null,
          tees: (h.tees || []).map((t) => ({ color: (t.color || "").toLowerCase(), yards: t.yards })),
        }));
        setHoles(initialHoles);
        // Initial tee columns = the union of tee colours across all holes,
        // sorted longest-first by their average yardage so the most-used tee
        // is the leftmost column.
        const colorSums = {};
        for (const h of initialHoles) {
          for (const t of h.tees) {
            colorSums[t.color] = (colorSums[t.color] || 0) + (t.yards || 0);
          }
        }
        setTeeCols(Object.keys(colorSums).sort((a, b) => (colorSums[b] || 0) - (colorSums[a] || 0)));
        setHolesDirty(false);
      } catch (err) {
        onError?.(formatApiError(err));
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [courseId, onClose, onError]);

  // Update one hole's par.
  const setPar = (idx, v) => {
    const next = [...holes];
    next[idx] = { ...next[idx], par: Math.max(3, Math.min(6, Number(v) || 4)) };
    setHoles(next);
    setHolesDirty(true);
  };

  // Update one hole's stroke index (1–18 or null).
  const setStrokeIndex = (idx, v) => {
    const raw = (v ?? "").toString();
    const next = [...holes];
    if (raw === "") {
      next[idx] = { ...next[idx], stroke_index: null };
    } else {
      const n = Math.max(1, Math.min(18, Number(raw) || 0));
      next[idx] = { ...next[idx], stroke_index: n || null };
    }
    setHoles(next);
    setHolesDirty(true);
  };

  // Auto-assign stroke index 1–18 from yardage. Convention used by most UK
  // clubs: hardest holes (longest) get the low SI, but odd indexes are
  // spread across the front 9 and evens across the back 9 so a fourball's
  // shots aren't all bunched into one half. Pure heuristic — the admin can
  // tweak any row afterwards.
  const suggestStrokeIndexes = () => {
    if (holes.length !== 18) {
      onError?.("Stroke index auto-suggest only works on full 18-hole courses.");
      return;
    }
    if (!window.confirm("Auto-assign stroke index 1–18 based on yardage? You can still hand-edit any row.")) return;
    // Rank holes by yardage descending (longest = hardest = lowest SI).
    const front = holes.slice(0, 9).map((h, i) => ({ idx: i, yards: h.yards || 0 })).sort((a, b) => b.yards - a.yards);
    const back  = holes.slice(9, 18).map((h, i) => ({ idx: i + 9, yards: h.yards || 0 })).sort((a, b) => b.yards - a.yards);
    // Interleave: front[0] = SI 1, back[0] = SI 2, front[1] = SI 3, …
    const siByIdx = {};
    for (let k = 0; k < 9; k++) {
      siByIdx[front[k].idx] = 2 * k + 1; // 1, 3, 5, 7, 9, 11, 13, 15, 17
      siByIdx[back[k].idx]  = 2 * k + 2; // 2, 4, 6, 8, 10, 12, 14, 16, 18
    }
    const next = holes.map((h, i) => ({ ...h, stroke_index: siByIdx[i] ?? h.stroke_index ?? null }));
    setHoles(next);
    setHolesDirty(true);
  };

  // Update one hole's per-tee yardage. The default `yards` always tracks the
  // longest tee on that hole so the cosmetic display stays sensible.
  const setTeeYards = (idx, color, v) => {
    const yds = Math.max(80, Math.min(750, Number(v) || 80));
    const next = [...holes];
    const teeList = [...(next[idx].tees || [])];
    const teeIdx = teeList.findIndex((t) => t.color === color);
    if (teeIdx >= 0) teeList[teeIdx] = { color, yards: yds };
    else teeList.push({ color, yards: yds });
    teeList.sort((a, b) => -(a.yards - b.yards));
    next[idx] = {
      ...next[idx],
      tees: teeList,
      yards: teeList[0]?.yards ?? next[idx].yards,
    };
    setHoles(next);
    setHolesDirty(true);
  };

  const addTeeColumn = (color) => {
    if (teeCols.includes(color)) return;
    setTeeCols([...teeCols, color]);
    // Seed each hole with a sensible default (use existing default yards)
    const next = holes.map((h) => {
      if (h.tees.some((t) => t.color === color)) return h;
      const seed = h.yards || 380;
      const teeList = [...h.tees, { color, yards: seed }].sort((a, b) => -(a.yards - b.yards));
      return { ...h, tees: teeList };
    });
    setHoles(next);
    setHolesDirty(true);
  };

  const removeTeeColumn = (color) => {
    if (!window.confirm(`Remove the ${color} tee from every hole? Per-hole ${color} yardages will be deleted.`)) return;
    setTeeCols(teeCols.filter((c) => c !== color));
    const next = holes.map((h) => {
      const teeList = h.tees.filter((t) => t.color !== color);
      teeList.sort((a, b) => -(a.yards - b.yards));
      return {
        ...h,
        tees: teeList,
        yards: teeList[0]?.yards ?? h.yards,
      };
    });
    setHoles(next);
    setHolesDirty(true);
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4_000_000) {
      onError?.("Photo too large — please pick something under 4MB.");
      return;
    }
    // Read as data URL so we can stash it on the course doc without needing
    // an external CDN. Same approach the rest of the app uses.
    const reader = new FileReader();
    reader.onload = () => {
      setImageUrl(reader.result);
      setImageDirty(true);
    };
    reader.readAsDataURL(file);
  };

  // Re-extract: snap a new scorecard photo, run it through Gemini Vision, and
  // dump the parsed par + per-tee yardages straight back into the editor's
  // holes/teeCols state so the admin can tweak before saving. Existing tees
  // not present on the new card are preserved (so you can add a missing
  // colour without nuking the rest).
  const onReExtract = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8_000_000) {
      onError?.("Scorecard photo too large — try one under 8MB.");
      return;
    }
    setReExtracting(true);
    setReExtractMsg("");
    try {
      const form = new FormData();
      form.append("image", file);
      const { data } = await api.post(`/courses/${courseId}/extract-scorecard`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // data: { par_total, yards_total, holes:[{number,par,yards}], tee_label, tees:[{label,holes:[{number,yards}]}], confidence, notes }
      const primary = (data.tee_label || (data.tees && data.tees[0]?.label) || "").toLowerCase();
      const newHoleCount = (data.holes || []).length;
      // Build the new holes array — keep existing tees that aren't covered by the new card.
      const existingHolesByNum = Object.fromEntries(holes.map((h) => [h.number, h]));
      const merged = (data.holes || []).map((h) => {
        const existing = existingHolesByNum[h.number] || { tees: [] };
        // Start from existing tees, then overlay the primary + any extracted extras.
        const teesMap = new Map((existing.tees || []).map((t) => [t.color, t.yards]));
        if (primary) teesMap.set(primary, h.yards);
        for (const tee of data.tees || []) {
          const label = (tee.label || "").toLowerCase();
          if (!label) continue;
          const m = (tee.holes || []).find((x) => x.number === h.number);
          if (m?.yards) teesMap.set(label, m.yards);
        }
        const teesArr = Array.from(teesMap, ([color, yards]) => ({ color, yards }))
          .sort((a, b) => -(a.yards - b.yards));
        return {
          number: h.number,
          par: h.par,
          yards: teesArr[0]?.yards ?? h.yards,
          tees: teesArr,
        };
      });
      setHoles(merged);
      // New tee column set: union of every colour now on any hole, sorted by total yardage descending.
      const sums = {};
      for (const h of merged) for (const t of h.tees) sums[t.color] = (sums[t.color] || 0) + t.yards;
      setTeeCols(Object.keys(sums).sort((a, b) => sums[b] - sums[a]));
      setHolesDirty(true);
      const teeCount = (Array.isArray(data.tees) ? data.tees.length : 0) || (primary ? 1 : 0);
      const conf = data.confidence || "medium";
      setReExtractMsg(
        conf === "low"
          ? `⚠ Read the card (${newHoleCount} holes, ${teeCount} tee${teeCount === 1 ? "" : "s"}) but I'm not confident — please double-check every row before saving.`
          : `✓ Re-extracted ${newHoleCount} holes, ${teeCount} tee${teeCount === 1 ? "" : "s"}. Review and tap Save changes.`,
      );
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setReExtracting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Pre-flight: stroke_index uniqueness check (better UX than waiting
      // for a 400 from the backend).
      if (holesDirty) {
        const siUsed = {};
        for (const h of holes) {
          if (h.stroke_index == null || h.stroke_index === "") continue;
          const si = Number(h.stroke_index);
          if (siUsed[si]) {
            onError?.(`Stroke index ${si} is on both hole ${siUsed[si]} and hole ${h.number}. Each index 1–18 must appear once.`);
            setSaving(false);
            return;
          }
          siUsed[si] = h.number;
        }
      }
      const body = { name: name.trim(), location: location.trim(), description: description.trim() };
      if (imageDirty) body.image_url = imageUrl;
      if (holesDirty) body.holes = holes.map((h) => ({
        number: h.number,
        par: h.par,
        yards: h.yards,
        tees: h.tees,
        ...(h.stroke_index != null && h.stroke_index !== "" ? { stroke_index: Number(h.stroke_index) } : {}),
      }));
      await api.patch(`/admin/courses/${courseId}`, body);
      onSaved?.();
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
      data-testid="edit-course-modal"
      onClick={() => !saving && onClose?.()}
    >
      <div
        className="surface-card max-w-2xl w-full p-5 my-auto max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-heading font-bold text-[var(--primary)] text-lg">Edit course</h3>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              Tweak tees, par/yards, the photo or course info — no scorecard re-upload needed.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-course-close"
            className="text-[var(--text-secondary)] hover:text-[var(--primary)] text-xl leading-none px-1"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-3 border-b border-[var(--border-default)]">
          {[
            { id: "info", label: "Info" },
            { id: "photo", label: "Photo" },
            { id: "holes", label: `Holes & Tees (${holes.length || "…"})` },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`edit-course-tab-${t.id}`}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[var(--primary)] text-[var(--primary)]"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--primary)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 text-center text-[var(--text-secondary)] text-sm">Loading course…</div>
        ) : (
          <>
            {tab === "info" && (
              <div className="space-y-3" data-testid="edit-course-info">
                <label className="block">
                  <span className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Name</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="edit-course-name"
                    className="input-field"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Location</span>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    data-testid="edit-course-location"
                    className="input-field"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Description</span>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="edit-course-description"
                    className="input-field"
                  />
                </label>
              </div>
            )}

            {tab === "photo" && (
              <div className="space-y-3" data-testid="edit-course-photo">
                <div className="rounded-xl overflow-hidden border border-[var(--border-default)] bg-[var(--accent-sand)] aspect-video flex items-center justify-center">
                  {imageUrl
                    ? <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
                    : <span className="text-xs text-[var(--text-secondary)]">No photo yet</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="edit-course-photo-pick"
                    className="btn-primary text-xs"
                  >
                    {imageUrl ? "Replace photo" : "Upload photo"}
                  </button>
                  {imageUrl && (
                    <button
                      type="button"
                      onClick={() => { setImageUrl(null); setImageDirty(true); }}
                      data-testid="edit-course-photo-clear"
                      className="btn-outline text-xs"
                    >
                      Remove photo
                    </button>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={onPickPhoto}
                    className="hidden"
                  />
                </div>
                <p className="text-[10px] text-[var(--text-secondary)] italic">
                  Tip: a wide landscape shot of the signature hole works best on the course card.
                </p>
              </div>
            )}

            {tab === "holes" && (
              <div className="space-y-3" data-testid="edit-course-holes">
                {/* Stroke index controls — used by Net Stableford + WHS pick-ups */}
                {(() => {
                  const total = holes.length;
                  const siVals = holes.map((h) => (h.stroke_index == null || h.stroke_index === "" ? null : Number(h.stroke_index)));
                  const filled = siVals.filter((v) => v != null).length;
                  const dupes = new Set();
                  const seen = new Set();
                  for (const v of siVals) {
                    if (v == null) continue;
                    if (seen.has(v)) dupes.add(v);
                    seen.add(v);
                  }
                  const missing = [];
                  if (total === 18) {
                    for (let i = 1; i <= 18; i++) if (!seen.has(i)) missing.push(i);
                  }
                  const ready = total >= 9 && filled === total && dupes.size === 0;
                  const canSuggest = total === 18;
                  return (
                    <div className="rounded-xl bg-[var(--primary)]/5 border border-[var(--primary)]/20 p-3" data-testid="stroke-index-card">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="text-xs font-bold text-[var(--primary)] uppercase tracking-wider">
                            Stroke index (handicap holes 1–{total === 9 ? 9 : 18})
                          </div>
                          <p className="text-[10px] text-[var(--text-secondary)] italic mt-0.5">
                            SI 1 = hardest hole; SI 18 = easiest. Used for Net Stableford + WHS pick-ups so high-handicappers get the right strokes on the right holes. Each value must appear once.
                          </p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-bold">
                            <span className={`px-2 py-0.5 rounded-full ${ready ? "bg-[var(--primary)] text-white" : "bg-[var(--accent-sand)] text-[var(--text-secondary)]"}`} data-testid="stroke-index-progress">
                              {filled}/{total} set
                            </span>
                            {dupes.size > 0 && (
                              <span className="px-2 py-0.5 rounded-full bg-[var(--accent-terracotta)] text-white" data-testid="stroke-index-dupes">
                                Duplicates: {Array.from(dupes).sort((a, b) => a - b).join(", ")}
                              </span>
                            )}
                            {missing.length > 0 && missing.length <= 6 && dupes.size === 0 && filled > 0 && (
                              <span className="px-2 py-0.5 rounded-full bg-[var(--accent-sand)] text-[var(--primary)]" data-testid="stroke-index-missing">
                                Missing: {missing.join(", ")}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={suggestStrokeIndexes}
                          disabled={!canSuggest}
                          data-testid="stroke-index-suggest"
                          className={`text-xs whitespace-nowrap shrink-0 ${canSuggest ? "btn-outline" : "btn-outline opacity-50 cursor-not-allowed"}`}
                          title={canSuggest ? "Auto-assign 1–18 by yardage (longest = SI 1, alternating front/back nine)" : "Suggest from yards is available on 18-hole courses only"}
                        >
                          Suggest from yards
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Re-extract from a fresh scorecard photo */}
                <div className="rounded-xl bg-[var(--primary)]/5 border border-[var(--primary)]/20 p-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <div className="text-xs font-bold text-[var(--primary)] uppercase tracking-wider">
                        Re-extract from photo
                      </div>
                      <p className="text-[10px] text-[var(--text-secondary)] italic mt-0.5">
                        Snap or upload a new scorecard photo and we'll dump the par + every tee colour back into this table for you to tweak. Existing tees not on the new card are kept.
                      </p>
                      {reExtractMsg && (
                        <div className="mt-1.5 text-[11px] font-bold text-[var(--primary)]">{reExtractMsg}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => reExtractFileRef.current?.click()}
                      disabled={reExtracting}
                      data-testid="edit-course-reextract"
                      className="btn-primary text-xs whitespace-nowrap shrink-0"
                    >
                      {reExtracting ? "Reading…" : "Re-extract"}
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={reExtractFileRef}
                      onChange={onReExtract}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Enrich pin coordinates from OpenStreetMap */}
                <OsmEnrichCard courseId={courseId} onEnriched={onSaved} onError={onError} />

                {/* Tee column manager */}
                <div className="rounded-xl bg-[var(--accent-sand)]/50 p-3">
                  <div className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                    Tee columns ({teeCols.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {TEE_PALETTE.map((opt) => {
                      const on = teeCols.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => (on ? removeTeeColumn(opt.id) : addTeeColumn(opt.id))}
                          data-testid={`edit-course-tee-toggle-${opt.id}`}
                          className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold border-2 transition-all ${
                            on
                              ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]"
                              : "border-[var(--text-secondary)]/30 text-[var(--text-secondary)]"
                          }`}
                        >
                          <span className="h-2.5 w-2.5 rounded-full border border-black/20" style={{ background: opt.swatch }} />
                          {opt.label}
                          {on && <span className="text-[9px] opacity-70">✕</span>}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] italic mt-1.5">
                    Tap a colour to add or remove its yardage column from every hole below.
                  </p>
                </div>

                {/* Per-hole editable table */}
                <div className="rounded-xl border border-[var(--border-default)] overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--secondary)] text-[var(--primary)] sticky top-0">
                      <tr>
                        <th className="py-1.5 px-2 text-left">#</th>
                        <th className="py-1.5 px-2">Par</th>
                        <th className="py-1.5 px-2" title="Stroke index — 1 hardest, 18 easiest">SI</th>
                        {teeCols.map((c) => {
                          const meta = TEE_PALETTE.find((o) => o.id === c);
                          return (
                            <th key={c} className="py-1.5 px-2 text-right whitespace-nowrap">
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2.5 w-2.5 rounded-full border border-black/20 inline-block" style={{ background: meta?.swatch || "#888" }} />
                                <span className="normal-case font-normal">{meta?.label || c}</span>
                              </span>
                            </th>
                          );
                        })}
                        {teeCols.length === 0 && <th className="py-1.5 px-2 text-right">Yds</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {holes.map((h, idx) => (
                        <tr key={h.number} className="border-t border-[var(--border-default)]">
                          <td className="py-1 px-2 font-mono-num font-bold text-[var(--primary)]">{h.number}</td>
                          <td className="py-1 px-2 text-center">
                            <select
                              value={h.par}
                              onChange={(e) => setPar(idx, e.target.value)}
                              data-testid={`edit-course-hole-${h.number}-par`}
                              className="input-field !py-1 !px-2 !text-xs text-center w-16"
                            >
                              {[3, 4, 5, 6].map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </td>
                          <td className="py-1 px-2 text-center">
                            {(() => {
                              const siVal = h.stroke_index == null ? "" : Number(h.stroke_index);
                              const siNums = holes.map((x) => (x.stroke_index == null || x.stroke_index === "" ? null : Number(x.stroke_index)));
                              const isDupe = siVal !== "" && siNums.filter((v) => v === siVal).length > 1;
                              const maxSi = holes.length === 9 ? 9 : 18;
                              return (
                                <select
                                  value={siVal}
                                  onChange={(e) => setStrokeIndex(idx, e.target.value)}
                                  data-testid={`edit-course-hole-${h.number}-si`}
                                  className={`input-field !py-1 !px-2 !text-xs text-center w-16 ${isDupe ? "!border-[var(--accent-terracotta)] !text-[var(--accent-terracotta)] font-bold" : ""}`}
                                  title={isDupe ? "Duplicate stroke index" : `Stroke index (1 hardest – ${maxSi} easiest)`}
                                >
                                  <option value="">—</option>
                                  {Array.from({ length: maxSi }, (_, k) => k + 1).map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              );
                            })()}
                          </td>
                          {teeCols.map((c) => {
                            const tee = h.tees.find((t) => t.color === c);
                            return (
                              <td key={c} className="py-1 px-2">
                                <input
                                  type="number"
                                  min={80}
                                  max={750}
                                  value={tee?.yards ?? ""}
                                  onChange={(e) => setTeeYards(idx, c, e.target.value)}
                                  data-testid={`edit-course-hole-${h.number}-${c}`}
                                  placeholder="—"
                                  className="input-field !py-1 !px-2 !text-xs text-right w-20"
                                />
                              </td>
                            );
                          })}
                          {teeCols.length === 0 && (
                            <td className="py-1 px-2">
                              <input
                                type="number"
                                min={80}
                                max={750}
                                value={h.yards}
                                onChange={(e) => {
                                  const next = [...holes];
                                  next[idx] = { ...next[idx], yards: Math.max(80, Math.min(750, Number(e.target.value) || 80)) };
                                  setHoles(next);
                                  setHolesDirty(true);
                                }}
                                data-testid={`edit-course-hole-${h.number}-yards-default`}
                                className="input-field !py-1 !px-2 !text-xs text-right w-20"
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-[var(--border-default)]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="edit-course-cancel"
            className="btn-outline text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            data-testid="edit-course-save"
            className="btn-primary text-xs"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}



// CountryImporterCard — admin one-click button to seed Spain/Portugal/etc
// into the live DB by hitting the Overpass API. Safe to re-run; duplicates
// (by exact name match) are skipped.
function CountryImporterCard({ onToast, onError }) {
  const [busy, setBusy] = useState(null); // country id while a request is in flight
  const [results, setResults] = useState({}); // { spain: { added, skipped_duplicates, total_courses_now }, ... }
  const COUNTRIES = [
    { id: "spain",    label: "Spain",    flag: "🇪🇸" },
    { id: "portugal", label: "Portugal", flag: "🇵🇹" },
    { id: "france",   label: "France",   flag: "🇫🇷" },
    { id: "ireland",  label: "Ireland",  flag: "🇮🇪" },
    { id: "germany",  label: "Germany",  flag: "🇩🇪" },
  ];

  const run = async (id) => {
    if (busy) return;
    setBusy(id);
    try {
      const { data } = await api.post("/admin/courses/import-country", { country: id });
      setResults((r) => ({ ...r, [id]: data }));
      onToast?.(
        data.added > 0
          ? `Added ${data.added} ${data.country} courses (DB now ${data.total_courses_now})`
          : `${data.country}: nothing new (${data.skipped_duplicates} duplicates skipped)`,
      );
    } catch (err) {
      onError?.(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="surface-card-flat p-5" data-testid="country-importer-card">
      <h3 className="font-heading font-bold text-[var(--primary)] mb-1">Import courses by country</h3>
      <p className="text-xs text-[var(--text-secondary)] mb-3">
        Pulls golf courses from OpenStreetMap and adds any that aren't already in the database. Safe to re-run — existing courses are skipped. Takes 20-90 seconds per country.
      </p>
      <div className="flex flex-wrap gap-2">
        {COUNTRIES.map((c) => {
          const res = results[c.id];
          const isBusy = busy === c.id;
          return (
            <button
              key={c.id}
              type="button"
              disabled={busy !== null}
              onClick={() => run(c.id)}
              data-testid={`import-country-${c.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-full border-2 text-xs font-bold font-heading transition-all ${
                res
                  ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]"
                  : "border-[var(--text-secondary)]/30 text-[var(--primary)] hover:border-[var(--primary)]"
              } ${isBusy ? "opacity-60" : ""} ${busy && !isBusy ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <span aria-hidden>{c.flag}</span>
              {isBusy ? `Importing ${c.label}…` : c.label}
              {res && !isBusy && (
                <span className="font-mono-num font-normal text-[10px] opacity-80">
                  +{res.added}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {Object.values(results).length > 0 && (
        <div className="mt-3 rounded-xl bg-[var(--accent-sand)]/40 p-3 text-[11px] text-[var(--text)] space-y-0.5" data-testid="country-import-results">
          {Object.entries(results).map(([id, r]) => (
            <div key={id} className="flex items-center justify-between gap-3">
              <span><strong>{r.country}:</strong> added {r.added}, skipped {r.skipped_duplicates} dup</span>
              <span className="opacity-70">DB total: {r.total_courses_now}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Admin-only card that runs the OSM pin enrichment for the currently-editing
 * course. Best when the DB has synthetic/placeholder pin coords rather than
 * real per-hole green locations. Uses the existing lib/osm_pins backend.
 */
function OsmEnrichCard({ courseId, onEnriched, onError }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function runEnrich() {
    setRunning(true);
    setResult(null);
    try {
      const res = await api.post(`/admin/courses/${courseId}/enrich-osm-pins`);
      setResult(res.data);
      if (res.data.updated > 0 && onEnriched) onEnriched();
    } catch (e) {
      onError?.(e.response?.data?.detail || "OSM enrichment failed. Try again in a minute.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rounded-xl bg-[var(--primary)]/5 border border-[var(--primary)]/20 p-3" data-testid="osm-enrich-card">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="text-xs font-bold text-[var(--primary)] uppercase tracking-wider">
            Pin locations from OpenStreetMap
          </div>
          <p className="text-[10px] text-[var(--text-secondary)] italic mt-0.5">
            One-tap fetch of real per-hole green coordinates from OSM. Overwrites any pin that&apos;s more than 20 yards off — synthetic placeholders get replaced with the real thing. Overpass API is rate-limited so this may take 5–15 seconds.
          </p>
          {result && (
            <div className="mt-1.5 text-[11px] font-bold" data-testid="osm-enrich-result">
              {result.status === "ok" && result.updated > 0 && (
                <span className="text-[var(--primary)]">
                  ✓ Updated {result.updated} hole{result.updated === 1 ? "" : "s"} (OSM matched {result.osm_matched}).
                </span>
              )}
              {result.status === "ok" && result.updated === 0 && (
                <span className="text-[var(--text-secondary)]">Existing pins already match OSM — nothing to change.</span>
              )}
              {result.status === "no_osm_data" && (
                <span className="text-[var(--accent-terracotta)]">OSM has no golf tags mapped for this course yet.</span>
              )}
              {result.status === "no_centroid" && (
                <span className="text-[var(--accent-terracotta)]">No existing pins to seed the search from — pin at least one hole manually first.</span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={runEnrich}
          disabled={running}
          data-testid="osm-enrich-run"
          className="btn-primary text-xs whitespace-nowrap shrink-0"
        >
          {running ? "Enriching…" : "Fetch from OSM"}
        </button>
      </div>
    </div>
  );
}
